/* eslint-disable @typescript-eslint/no-explicit-any */
import { FieldValues, UseFormReturn } from 'react-hook-form';
import { FormContainer } from 'react-hook-form-mui';
import { assign, fromPromise, setup } from 'xstate';
import { Stack } from '@mui/material';

import { noop } from '@barboxx/utils';

import {
    BasicDialog,
    BasicDialogProps,
    SubmitButtonProps,
} from './BasicDialog';

interface ExecutorProps {
    dataToSubmit?: any;
    executor?: (data?: any) => Promise<any>;
    onSuccess?: (data?: any) => void;
    onError?: (data?: any) => void;
}

export interface DialogMachineContext extends ExecutorProps {
    title?: string;
    submitButtonProps?: SubmitButtonProps;
    cancelButton?: string;
    Component?: React.ReactNode | string;
    initialValues?: any;
    error?: string;
}

export type DialogEvent =
    | { type: 'OPEN'; data: DialogMachineContext }
    | { type: 'EXECUTING'; data: any }
    | { type: 'DISMISS'; error?: string };

export type { SubmitButtonProps };

export const dialogMachine = setup({
    types: {
        context: {} as DialogMachineContext,
        events: {} as DialogEvent,
    },
    actors: {
        executeAction: fromPromise(
            async ({ input }: { input: ExecutorProps }) => {
                const { executor, dataToSubmit, onSuccess, onError } = input;
                try {
                    const response = await executor?.(dataToSubmit);

                    onSuccess?.(response.data);
                } catch (e) {
                    onError?.(e);
                }
            }
        ),
    },
    actions: {
        clear: assign({
            title: undefined,
            submitButtonProps: undefined,
            cancelButton: undefined,
            Component: undefined,
            initialValues: undefined,
            dataToSubmit: undefined,
            executor: undefined,
            onSuccess: undefined,
            onError: undefined,
            error: '',
        }),
        onError: assign({
            // error: ({ event }) => {
            //     assertEvent(event, 'DISMISS');
            //     return {
            //         error: event?.error,
            //     };
            // },
        }),
    },
}).createMachine({
    id: 'dialog-machine',
    initial: 'closed',
    context: {},
    states: {
        closed: {
            id: 'closed',
            on: {
                OPEN: {
                    target: 'open',
                    actions: assign({
                        // Visuals
                        title: ({ event }) => event.data?.title,
                        Component: ({ event }) => event.data?.Component,
                        submitButtonProps: ({ event }) =>
                            event.data?.submitButtonProps,
                        cancelButton: ({ event }) => event.data?.cancelButton,
                        // Form data
                        initialValues: ({ event }) => event.data?.initialValues,
                        // Async actions
                        executor: ({ event }: any) => event.data?.executor,
                        onSuccess: ({ event }: any) => event.data?.onSuccess,
                        onError: ({ event }: any) => event.data?.onError,
                    }),
                },
            },
        },
        open: {
            id: 'open',
            initial: 'idle',
            states: {
                idle: {
                    on: {
                        EXECUTING: {
                            target: 'executing',
                            actions: assign({
                                dataToSubmit: ({ event }) => event.data,
                            }),
                        },
                        DISMISS: {
                            target: '#closed',
                            actions: ['clear'],
                        },
                    },
                },
                executing: {
                    invoke: {
                        id: 'execute-dialog',
                        src: 'executeAction',
                        input: ({ event, context }) => ({
                            executor: context.executor,
                            onSuccess: context.onSuccess,
                            onError: context.onError,
                            dataToSubmit: context.dataToSubmit,
                        }),
                        onError: {
                            target: 'idle',
                            actions: 'onError',
                        },
                        onDone: {
                            target: '#closed',
                            actions: ['clear'],
                        },
                    },
                },
            },
        },
    },
});

export interface HookFormProps<T extends FieldValues> extends BasicDialogProps {
    formContext: UseFormReturn<T>;
}

export function HookFormsFlow<T extends FieldValues>({
    open,
    children,
    formContext,
    onSubmit = noop,
    submitButtonProps,
    ...rest
}: HookFormProps<T>) {
    return (
        <BasicDialog
            {...rest}
            open={open}
            onSubmit={formContext.handleSubmit(onSubmit)}
            submitButtonProps={{
                disabled:
                    !formContext.formState.isDirty ||
                    formContext.formState.isSubmitting,
                ...submitButtonProps,
            }}
            processing={formContext.formState.isSubmitting}
        >
            <FormContainer formContext={formContext} onSuccess={onSubmit}>
                <Stack paddingTop={1}>{children}</Stack>
            </FormContainer>
        </BasicDialog>
    );
}

HookFormsFlow.Text = BasicDialog.Text;
/* eslint-enable @typescript-eslint/no-explicit-any */
